import { Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import 'assets/scripts/lrm-pgrouting.js';
import 'leaflet';
import 'leaflet-extra-markers';
import 'leaflet-routing-machine';
import { Observable } from 'rxjs/Observable';

import { CBMError, Contract, ContractSettings, Siterecord, SiterecordCharacteristics } from 'app/models';
import { ComputationMethodType, EquipmentType, LocationType, MeasurementValueTypeLinear } from 'app/models/enums/';
import { ContractService, ContractSettingsService, ErrorService, MapService, SiterecordService } from 'app/services';
import { MessageboxComponent } from 'app/shared/messagebox/messagebox.component';
import { enumValues } from 'app/shared/util/enumValues';

declare const L: any;

@Component({
  selector: 'app-map',
  templateUrl: './linear-insert.component.html',
  styleUrls: ['./linear-insert.component.scss']
})
export class LinearInsertComponent implements OnInit {
  @ViewChild('locationForm') public form: NgForm = null;
  @ViewChild(MessageboxComponent) messagebox: MessageboxComponent;

  private map: L.Map;
  private control: any;
  private layerControl: any;

  public location: Siterecord;
  public contract: Contract;
  public contractSettings: ContractSettings;
  public prefix: string;
  public recordId: string;

  public previousWaypoints: any[];
  public routeFound = false;
  public noRouteFoundTranslation: string;
  public failedSaveTranslation: string;

  public errorMessages: CBMError[];

  public newLocationAfterSave: boolean;
  public isSaving = false;

  public prefixes: any;
  public type: string;

  public computationMethods: any;
  public equipmentTypes: any;

  public startLatitude: string;
  public startLongitude: string;
  public endLatitude: string;
  public endLongitude: string;

  public singleColumnStyle: any = { label: 'col-xs-4', control: 'col-xs-8' };

  private greenMarker;
  private redMarker;
  private blueMarker;

  private hectoPuntenLayer: any;
  private hectopuntenLayerTranslation: string;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly siterecordService: SiterecordService,
    private readonly contractService: ContractService,
    private readonly contractSettingsService: ContractSettingsService,
    private readonly translateService: TranslateService,
    private readonly mapService: MapService,
    private readonly errorService: ErrorService,
    private readonly ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.errorService.linearInsertComponent = this;

    this.errorMessages = [];
    this.newLocationAfterSave = false;
    this.initialiseMap();
    this.initialiseLayerNames();
    this.initialiseLayerControl();
    this.initialiseEnums();
    this.initialiseErrorMessage();

    this.route.params.subscribe(params => {
      const lat = params['lat'];
      const lng = params['lng'];

      if (lat && lng) {
        this.form.resetForm();
        this.addStartMarker({ lat, lng });
        this.map.setView([lat, lng], 12);
      } else {
        this.map.setView([this.mapService.defaultLat, this.mapService.defaultLng], this.mapService.defaultZoom);
      }

      if (!this.contract) {
        const contractId = params['contractId'];
        this.contractService.getContract(contractId).subscribe(contract => {
          this.contract = contract;
          this.initialiseNewLocation();
        });
      } else {
        this.initialiseNewLocation();
      }
    });

    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.initialiseLayerNames();
      this.initialiseLayerControl();
      this.initialiseErrorMessage();
    });
  }

  private initialiseContractSettings(contractId: any): void {
    if (!this.contractSettings) {
      this.contractSettingsService.getContractSettings(contractId).subscribe(contractSettings => {
        this.contractSettings = contractSettings;
        if (this.contractSettings.linearComputationMethod) {
          this.location.computationMethod = this.contractSettings.linearComputationMethod;
        }
        if (this.contractSettings.linearAccuracy) {
          this.location.siterecordCharacteristics[0].accuracy = this.contractSettings.linearAccuracy;
        }
        if (this.contractSettings.linearPeriod) {
          this.location.siterecordCharacteristics[0].period = this.contractSettings.linearPeriod;
        }
      });
    }
  }

  private initialiseLayerNames(): void {
    this.translate('SITERECORD.HECTOPUNTENLAYER').subscribe(
      translation => (this.hectopuntenLayerTranslation = translation)
    );
  }

  private initialiseErrorMessage(): void {
    this.translate('ERROR.FE001').subscribe(translation => (this.noRouteFoundTranslation = translation));

    this.translate('ERROR.FE002').subscribe(translation => (this.failedSaveTranslation = translation));
  }

  private translate(valueToTranslate: string): Observable<string> {
    return this.translateService.get(valueToTranslate);
  }

  private initialiseLayerControl(): void {
    if (!this.layerControl) {
      this.layerControl = L.control.layers({}, {}, { position: 'topright' }).addTo(this.map);
    }
    this.layerControl.removeLayer(this.hectoPuntenLayer);
    this.layerControl.addOverlay(this.hectoPuntenLayer, this.hectopuntenLayerTranslation);
  }

  private initialiseMap(): void {
    this.map = L.map('map');

    L.tileLayer(this.mapService.osmTilesUrl, { maxZoom: 20, maxNativeZoom: 16 }).addTo(this.map);

    this.hectoPuntenLayer = L.tileLayer(this.mapService.hectometreTilesUrl, { maxZoom: 19 });
    this.hectoPuntenLayer.addTo(this.map);

    this.blueMarker = L.ExtraMarkers.icon({
      markerColor: 'blue',
      shape: 'circle'
    });

    this.greenMarker = L.ExtraMarkers.icon({
      markerColor: 'green',
      shape: 'circle'
    });

    this.redMarker = L.ExtraMarkers.icon({
      markerColor: 'red',
      shape: 'circle'
    });

    this.control = L.Routing.control({
      createMarker: (i, dStart, n) => this.placeMarkers(i, dStart, n),
      routeWhileDragging: false,
      waypointMode: 'snap',
      autoRoute: false,
      router: new L.Routing.PgRouting('cbmdb', { serviceUrl: this.mapService.geoserverWfsUrl })
    }).addTo(this.map);

    this.control.on('waypointschanged', e => {
      // Use ngZone to catch all leaflet and leaflet routing machine errors
      this.ngZone.runGuarded(() => this.control.route());
    });

    this.control.on('routeselected', e => {
      const waypoints = this.control.getWaypoints();
      this.routeFound = true;

      if (waypoints[0].latLng != null) {
        this.startLatitude = waypoints[0].latLng.lat;
        this.startLongitude = waypoints[0].latLng.lng;
      } else {
        this.routeFound = false;
      }

      if (waypoints[waypoints.length - 1].latLng != null) {
        this.endLatitude = waypoints[waypoints.length - 1].latLng.lat;
        this.endLongitude = waypoints[waypoints.length - 1].latLng.lng;
      } else {
        this.routeFound = false;
      }

      if (this.routeFound) {
        this.previousWaypoints = [];
        for (const waypoint of waypoints) {
          this.previousWaypoints.push(L.latLng(waypoint.latLng.lat, waypoint.latLng.lng));
        }
      } else if (!this.routeFound) {
        this.errorFindingRoute();
      }
    });

    this.control.on('routingerror', e => this.errorFindingRoute());

    this.map.on('click', (e: any) => {
      if (!this.control.getWaypoints()[0].latLng) {
        this.addStartMarker(e.latlng);
      } else if (!this.control.getWaypoints()[1].latLng) {
        this.addEndMarker(e.latlng);
      }
    });
  }

  public errorFindingRoute(): void {
    if (this.previousWaypoints && this.previousWaypoints.length > 0) {
      this.control.setWaypoints(this.previousWaypoints);
    }
    alert(this.noRouteFoundTranslation);
  }

  private initialiseNewLocation(): void {
    this.location = new Siterecord();

    this.location.contractId = this.contract.id;
    this.location.locationType = LocationType[LocationType.linear];
    this.location.routingPoints = '';
    this.location.siterecordCharacteristics = [new SiterecordCharacteristics()];
    this.location.siterecordCharacteristics[0].measurementValueType =
      MeasurementValueTypeLinear[MeasurementValueTypeLinear.travelTimeInformation];

    this.location.active = false;
    this.location.siterecordCharacteristics[0].characteristicsOrder = 1;
    this.location.siterecordCharacteristics[0].measurementCategory = 'non_classifiable';

    this.prefixes = this.contract.prefix.split(';');

    if (this.prefixes.length === 1) {
      this.setRecordIdPrefix(this.prefixes[0]);
    }

    this.type = this.contract.contractType;

    if (this.prefixes.length === 1) {
      this.setRecordIdPrefix(this.prefixes[0]);
    }
    this.type = this.contract.contractType;

    if (this.type === 'FCD') {
      this.location.computationMethod = 'arithmeticAverageOfSamplesInATimePeriod';
      this.location.siterecordCharacteristics[0].accuracy = 100;
      this.location.siterecordCharacteristics[0].period = 60;
      this.location.numberOfLanes = 1;
      this.location.equipmentTypeUsed = 'fcd';
    }

    this.initialiseContractSettings(this.contract.id);
  }

  private placeMarkers(i, dStart, n): void {
    if (this.routeFound) {
      this.errorMessages = [];
    }
    const marker = L.marker(dStart.latLng, {
      draggable: true,
      icon: i === 0 ? this.greenMarker : i === n - 1 ? this.redMarker : this.blueMarker
    });

    if (i !== 0 && i !== n - 1) {
      const container = L.DomUtil.create('div');
      const deleteButton = L.DomUtil.create('button', '', container);
      deleteButton.setAttribute('type', 'button');
      deleteButton.setAttribute('class', 'btn btn-danger btn-xs');
      deleteButton.innerHTML = this.translateService.instant('BUTTON.DELETE');

      marker.bindPopup(container);

      L.DomEvent.on(deleteButton, 'click', e => {
        this.control.spliceWaypoints(i, 1);
        this.map.closePopup();
      });
    }
    return marker;
  }

  private initialiseEnums(): void {
    this.computationMethods = enumValues(ComputationMethodType);
    this.equipmentTypes = enumValues(EquipmentType);
  }

  private addStartMarker(latlng): void {
    this.control.setWaypoints([L.latLng(latlng.lat, latlng.lng)]);
    this.startLatitude = latlng.lat;
    this.startLongitude = latlng.lng;
  }

  private addEndMarker(latlng): void {
    this.control.spliceWaypoints(this.control.getWaypoints().length - 1, 1, L.latLng(latlng.lat, latlng.lng));
    this.endLatitude = latlng.lat;
    this.endLongitude = latlng.lng;
  }

  public changeStartMarker(): void {
    if (this.startLatitude && this.startLongitude) {
      const latlng = L.latLng(this.startLatitude, this.startLongitude);
      this.control.spliceWaypoints(0, 1, latlng);
    }
  }

  public changeEndMarker(): void {
    if (this.endLatitude && this.endLongitude) {
      const latlng = L.latLng(this.endLatitude, this.endLongitude);
      this.control.spliceWaypoints(this.control.getWaypoints().length - 1, 1, latlng);
    }
  }

  public setRecordIdPrefix(prefix: string): void {
    this.prefix = prefix;
    const recordId = this.location.recordId;
    this.location.recordId =
      recordId == null || recordId === ''
        ? `${this.prefix}_`
        : this.recordId
        ? `${this.prefix}_${this.recordId}`
        : `${this.prefix}_`;
  }

  public setRecordId(): void {
    const recordId = this.location.recordId;
    this.location.recordId =
      recordId == null || recordId === ''
        ? this.recordId
        : this.prefix
        ? `${this.prefix}_${this.recordId}`
        : this.recordId;
  }

  public onSubmit(): void {
    this.isSaving = true;
    this.errorMessages = [];

    this.location.routingPoints = this.control
      .getWaypoints()
      .map(waypoint => `${waypoint.latLng.lng}\\,${waypoint.latLng.lat}`)
      .join('|');

    this.siterecordService.saveSiterecord(this.location).subscribe(
      response => {
        if (response.ok) {
          if (this.newLocationAfterSave) {
            this.router.navigate([
              `/location/${this.location.contractId}/linear/new/${this.endLatitude}/${this.endLongitude}`
            ]);
          } else {
            this.router.navigate([`/locations/${this.location.contractId}`]);
          }
        }
        this.isSaving = false;
      },
      (errors: CBMError[]) => {
        if (errors.length === 1 && errors[0].code === '500') {
          alert(this.failedSaveTranslation);
        } else {
          this.errorMessages = errors;
        }
        this.isSaving = false;
      }
    );
  }

  public onCancel(): void {
    this.router.navigate([`/locations/${this.location.contractId}`]);
  }
}



// WEBPACK FOOTER //
// ./src/app/locations/insert/linear/linear-insert.component.ts